import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { downloadOfferPdf } from "@/lib/api";
import { euro } from "@/lib/format";
import { statusInfo, daysWaiting } from "@/lib/status";
import { Download, FileText, Clock, Target, Wallet, Layers, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={16} />
        <span className="text-xs uppercase tracking-widest font-bold">{label}</span>
      </div>
      <p className="text-3xl font-extrabold text-navy mt-2">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminOffers() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [o, s] = await Promise.all([api.get("/offers"), api.get("/stats")]);
      setOffers(o.data);
      setStats(s.data);
      setLoading(false);
    })();
  }, []);

  const pdf = async (o, e) => {
    e.stopPropagation();
    try { await downloadOfferPdf(o.id, o.client_name); } catch { toast.error("Errore PDF"); }
  };

  const load = async () => {
    const [o, s] = await Promise.all([api.get("/offers"), api.get("/stats")]);
    setOffers(o.data);
    setStats(s.data);
  };

  const clearAll = async () => {
    if (!window.confirm("Eliminare TUTTE le offerte? L'operazione non è reversibile.")) return;
    if (!window.confirm("Confermi definitivamente lo svuotamento di tutte le offerte?")) return;
    try {
      const { data } = await api.delete("/offers");
      toast.success(`${data.deleted} offerte eliminate`);
      await load();
    } catch {
      toast.error("Errore durante lo svuotamento");
    }
  };

  const ov = stats?.overall;

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <h1 className="text-3xl font-extrabold text-navy tracking-tight">Andamento offerte</h1>
        {offers.length > 0 && (
          <Button variant="outline" onClick={clearAll} data-testid="clear-all-offers-button"
            className="gap-2 font-bold text-destructive border-destructive/40 hover:bg-destructive/10">
            <Trash2 size={16} /> Svuota tutte le offerte
          </Button>
        )}
      </div>
      <p className="text-muted-foreground mb-8">Monitora conversioni, tempi di attesa e valore venduto dei commerciali.</p>

      {ov && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi icon={Clock} label="Tempo medio conversione" value={ov.avg_conversion_days != null ? `${ov.avg_conversion_days}g` : "—"} sub="Sulle offerte vinte" />
            <Kpi icon={Target} label="Tasso conversione" value={`${ov.conversion_rate}%`} sub={`${ov.won} vinte su ${ov.total}`} />
            <Kpi icon={Wallet} label="Valore venduto" value={`${euro(ov.sold_monthly)}/mese`} sub={ov.sold_oneoff > 0 ? `+ ${euro(ov.sold_oneoff)} una tantum` : "Ricorrente"} />
            <Kpi icon={Layers} label="Offerte" value={ov.total} sub={`${ov.open} aperte · ${ov.won} vinte · ${ov.lost} perse`} />
          </div>

          {stats.per_agent.length > 0 && (
            <div className="bg-white border border-border rounded-xl overflow-hidden mb-8">
              <div className="px-5 py-3 border-b border-border bg-secondary">
                <p className="font-bold text-navy">Per commerciale</p>
              </div>
              <table className="w-full text-sm">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left font-bold px-5 py-3">Commerciale</th>
                    <th className="text-right font-bold px-5 py-3">Offerte</th>
                    <th className="text-right font-bold px-5 py-3">Vinte</th>
                    <th className="text-right font-bold px-5 py-3 hidden sm:table-cell">Tasso</th>
                    <th className="text-right font-bold px-5 py-3 hidden md:table-cell">Tempo medio</th>
                    <th className="text-right font-bold px-5 py-3">Venduto/mese</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.per_agent.map((a, i) => (
                    <tr key={i} data-testid={`agent-stat-${i}`} className="border-t border-border">
                      <td className="px-5 py-3 font-bold text-navy">{a.agent_name}</td>
                      <td className="px-5 py-3 text-right text-navy">{a.total}</td>
                      <td className="px-5 py-3 text-right text-navy">{a.won}</td>
                      <td className="px-5 py-3 text-right text-navy hidden sm:table-cell">{a.conversion_rate}%</td>
                      <td className="px-5 py-3 text-right text-navy hidden md:table-cell">{a.avg_conversion_days != null ? `${a.avg_conversion_days}g` : "—"}</td>
                      <td className="px-5 py-3 text-right font-extrabold text-navy">{euro(a.sold_monthly)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <h2 className="font-extrabold text-navy text-xl mb-3">Tutte le offerte</h2>
      {loading ? (
        <p className="text-muted-foreground">Caricamento...</p>
      ) : offers.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-16 text-center bg-white">
          <FileText className="mx-auto text-muted-foreground mb-3" size={40} />
          <p className="text-navy font-bold">Nessuna offerta generata</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-muted-foreground">
              <tr>
                <th className="text-left font-bold px-5 py-3">Cliente</th>
                <th className="text-left font-bold px-5 py-3 hidden md:table-cell">Commerciale</th>
                <th className="text-left font-bold px-5 py-3">Stato</th>
                <th className="text-right font-bold px-5 py-3 hidden sm:table-cell">Giorni attesa</th>
                <th className="text-left font-bold px-5 py-3 hidden lg:table-cell">Data vendita</th>
                <th className="text-right font-bold px-5 py-3">Mensile</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => (
                <tr key={o.id} data-testid={`admin-offer-${o.id}`}
                  onClick={() => navigate(`/offerte/${o.id}`)}
                  className="border-t border-border hover:bg-secondary cursor-pointer transition-colors duration-200">
                  <td className="px-5 py-3 font-bold text-navy">{o.client_name}<div className="text-xs text-muted-foreground font-normal">{o.agency_name}</div></td>
                  <td className="px-5 py-3 text-navy hidden md:table-cell">{o.agent_name || o.agent_email}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold border ${statusInfo(o.status).cls}`}>{statusInfo(o.status).label}</span>
                  </td>
                  <td className="px-5 py-3 text-right text-navy hidden sm:table-cell">{daysWaiting(o)}g</td>
                  <td className="px-5 py-3 text-primary font-semibold hidden lg:table-cell">{o.won_at ? o.won_at.slice(0, 10) : "—"}</td>
                  <td className="px-5 py-3 text-right font-extrabold text-navy">
                    {euro(o.totals?.monthly_final != null ? o.totals.monthly_final : o.totals?.monthly_net)}
                    {o.totals?.agency_discount > 0 && (
                      <div className="text-xs font-bold text-primary" data-testid={`admin-agency-discount-${o.id}`}>
                        Sconto agenzia − {euro(o.totals.agency_discount)}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={(e) => pdf(o, e)} className="text-muted-foreground hover:text-primary transition-colors duration-200" data-testid={`admin-pdf-${o.id}`}>
                      <Download size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
